import React, { useEffect, useState, useRef } from 'react';
import SendMessage from './modal/send_message-test.modal';
import moment from 'moment';
import Icon from '../../components/forms/icon.component';
import { useLocation } from 'react-router-dom';
import { extractPreviewFromTemplate } from '../../pages/templates/rcs/templates-rcs.page';
import { useDarkMode } from '../../hooks/use-dark-mode.hook';

interface Props {
  data?: any;
  payload?: any;
  sticky?: boolean;
  type?: string;
  exmplesCampaigns?: any;
}

const MockupAndroid = React.forwardRef<HTMLDivElement, Props>(
  ({ type = '', data, payload, exmplesCampaigns }, ref) => {
    const isDarkTheme = useDarkMode();
    const onlyCardRef = useRef<HTMLDivElement | null>(null);
    const suggestionsRef = useRef<HTMLDivElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [startY, setStartY] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const carrouselRefs = useRef<(HTMLDivElement | null)[]>([]);
    const getFormattedTime = () => moment().format('h:mm A');
    const validPaths = [
      '/sendCampaigns/flows-campaigns',
      '/sendCampaigns/fallback-campaigns',
      '/sendCampaigns/standard-campaigns'
    ];
    const location = useLocation();
    const isValidRoute = validPaths.includes(location.pathname);
    const routeFallback = location.pathname === '/sendCampaigns/fallback-campaigns';
    const [showModal, setShowModal] = useState({
      show: false,
      data: { typeModal: 'sms' }
    });

    const getTemplatePreview = (template: any) => {
      if (template?.preview && template.preview.length > 0) {
        return template.preview;
      }

      return extractPreviewFromTemplate(template);
    };

    const getContextualData = () => {
      if (data?.elements && Array.isArray(data.elements)) {
        return {
          elements: data.elements,
          suggestions: data.suggestions || [],
          context: 'builder'
        };
      }

      if (payload) {
        const templateData = getTemplatePreview(payload);

        const extractSuggestionsFromPayload = (payload: any) => {
          if (payload.type === 'BASIC') {
            return [];
          }
          if (payload?.preview && Array.isArray(payload.preview) && payload.preview.length > 0) {
            const lastPreviewElement = payload.preview[payload.preview.length - 1];
            if (lastPreviewElement?.suggestions && lastPreviewElement.suggestions.length > 0) {
              return lastPreviewElement.suggestions;
            }
          }

          if (payload?.single && Array.isArray(payload.single) && payload.single.length > 0) {
            const lastSingleElement = payload.single[payload.single.length - 1];
            if (lastSingleElement?.suggestions && lastSingleElement.suggestions.length > 0) {
              return lastSingleElement.suggestions.map((suggestion: any) => ({
                type: 'answer',
                gnlLabel: suggestion.reply?.text || '',
                linkURL: '',
                phoneNumber: '',
                label: '',
                latitude: 0,
                longitude: 0,
                startTime: '',
                endTime: '',
                titleC: '',
                descriptionC: ''
              }));
            }
          }
          if (
            payload?.conversational &&
            Array.isArray(payload.conversational) &&
            payload.conversational.length > 0
          ) {
            const lastConversationalElement =
              payload.conversational[payload.conversational.length - 1];
            if (
              lastConversationalElement?.suggestions &&
              lastConversationalElement.suggestions.length > 0
            ) {
              return lastConversationalElement.suggestions.map((suggestion: any) => ({
                type: 'answer',
                gnlLabel: suggestion.reply?.text || '',
                linkURL: '',
                phoneNumber: '',
                label: '',
                latitude: 0,
                longitude: 0,
                startTime: '',
                endTime: '',
                titleC: '',
                descriptionC: ''
              }));
            }
          }

          return [];
        };

        const suggestions = extractSuggestionsFromPayload(payload);

        return {
          elements: templateData,
          suggestions: suggestions,
          context: 'preview'
        };
      }

      return {
        elements: [],
        suggestions: [],
        context: 'empty'
      };
    };

    const handleShowModal = async () => {
      setShowModal({ show: false, data: { typeModal: 'sms' } });
    };

    const handleMouseDown = (
      e: React.MouseEvent<HTMLDivElement>,
      containerType: 'carrousel' | 'onlyCard' | 'suggestions',
      index?: number
    ) => {
      if (containerType === 'carrousel' && index !== undefined && carrouselRefs.current[index]) {
        setIsDragging(true);
        setStartX(e.clientX);
        setScrollLeft(carrouselRefs.current[index]?.scrollLeft || 0);
      } else if (containerType === 'onlyCard' && onlyCardRef.current) {
        setIsDragging(true);
        setStartY(e.clientY);
        setScrollTop(onlyCardRef.current.scrollTop);
      } else if (containerType === 'suggestions' && suggestionsRef.current) {
        setIsDragging(true);
        setStartX(e.clientX);
        setScrollLeft(suggestionsRef.current.scrollLeft);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const activeCarrousel = carrouselRefs.current.find((ref) => ref?.contains(e.target as Node));
      if (activeCarrousel) {
        const walkX = (e.clientX - startX) * 1;
        activeCarrousel.scrollLeft = scrollLeft - walkX;
      }
      if (onlyCardRef.current) {
        const walkY = (e.clientY - startY) * 1;
        onlyCardRef.current.scrollTop = scrollTop - walkY;
      }
      if (suggestionsRef.current && suggestionsRef.current.contains(e.target as Node)) {
        const walkX = (e.clientX - startX) * 1;
        suggestionsRef.current.scrollLeft = scrollLeft - walkX;
      }
    };

    const handleMouseUp = () => setIsDragging(false);

    const isValidImageUrl = (url: string | undefined) => {
      if (!url) return false;
      try {
        const parsedUrl = new URL(url);
        return /\.(png|jpg|jpeg|gif)$/i.test(parsedUrl.pathname);
      } catch {
        return false;
      }
    };

    const replaceVariables = (text: string, examples: any[]): string => {
      if (!text || !examples || !Array.isArray(examples)) return text;
      const replace = (str: string): string => {
        return str.replace(/{{([^{}]+)}}/g, (_, variable) => {
          const found = examples.find((item) => item.header === variable.trim());
          return found?.value || `{{${variable}}}`;
        });
      };
      let result = replace(text);
      let replacedOnce;
      do {
        replacedOnce = result;
        result = replace(replacedOnce);
      } while (replacedOnce !== result);

      return result;
    };

    const getActionIcon = (type: string) => {
      switch (type) {
        case 'redirect':
          return 'bi bi-globe-americas';
        case 'call':
          return 'bi bi-telephone-fill';
        case 'location':
          return 'bi bi-geo-alt-fill';
        case 'calendar':
          return 'bi bi-calendar-event-fill';
        default:
          return '';
      }
    };

    const renderRichCardFromConversational = (item: any, index: number) => {
      const richCard = item.richCard?.standaloneCard;
      if (!richCard) return null;

      const content = richCard.cardContent;

      return (
        <div key={index} className="onlycard-container mb-10 w-100">
          <div className={isDarkTheme ? 'preview-template-RCS-dark' : 'preview-template-RCS'}>
            {content.media?.contentInfo?.fileUrl && (
              <div className="preview-template-img-container-url-contentinfo mb-5">
                <img
                  alt="Uploaded"
                  className="preview-cideo mw-100 mb-3"
                  src={content.media.contentInfo.fileUrl}
                />
              </div>
            )}
            <div className="preview-template-header fw-bolder text-dark">
              {replaceVariables(content.title, exmplesCampaigns)}
            </div>
            <p className="mb-10 text-muted fw-bolder">
              {replaceVariables(content.description, exmplesCampaigns)}
            </p>

            {/* Acciones del rich card */}
            {content.suggestions?.map((suggestion: any, i: number) => (
              <div className="text-center" key={`action-${i}`}>
                <div className="d-flex align-items-center justify-content-center gap-2 text-info">
                  <i className="bi bi-globe-americas text-info fw-bolder" />
                  <span className="fw-bolder">
                    {replaceVariables(suggestion.action?.text, exmplesCampaigns)}
                  </span>
                </div>
                {i !== content.suggestions.length - 1 && <hr className="w-100 mx-0 my-2" />}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderRichCardFromPreview = (element: any, index: number) => {
      return (
        <div key={index} className="onlycard-container mb-10">
          <div className={isDarkTheme ? 'preview-template-RCS-dark' : 'preview-template-RCS'}>
            {(element.imageCheck || element.videoCheck) && (
              <div className="preview-template-img-container-url mb-5">
                <img
                  alt="Uploaded"
                  className="preview-cideo mw-100 mb-3"
                  src={
                    element.videoCheck && element.URLImagePoster === ''
                      ? '/assets/media/mockups/preview-video.png'
                      : element.imageCheck && element.URLImage === ''
                        ? '/assets/media/mockups/preview-img.png'
                        : element.videoCheck
                          ? element.URLImagePoster
                          : element.URLImage
                  }
                />
              </div>
            )}
            <div className="preview-template-header fw-bolder text-dark">
              {replaceVariables(element?.title, exmplesCampaigns)}
            </div>
            <p className="mb-10 text-muted fw-bolder">
              {replaceVariables(element?.description, exmplesCampaigns)}
            </p>
            {element?.labelURL && (
              <div className="text-center">
                <p className="text-info">
                  <i className="bi bi-globe-americas text-info fw-bolder" />
                  <span className="ms-2 fw-bolder">
                    {replaceVariables(element?.labelURL, exmplesCampaigns)}
                  </span>
                </p>
              </div>
            )}
            {Array.isArray(element.actions) &&
              element.actions.map((action: any, i: number) => {
                const iconClass = getActionIcon(action.type);
                const label = replaceVariables(action.gnlLabel, exmplesCampaigns);

                return (
                  <div className="text-center" key={`action-${i}`}>
                    <div className="d-flex align-items-center justify-content-center gap-2 text-info">
                      {iconClass && <i className={`${iconClass} text-info fw-bolder`} />}
                      {label && (
                        <span className="fw-bolder">
                          {replaceVariables(label, exmplesCampaigns)}
                        </span>
                      )}
                    </div>
                    {i !== element.actions.length - 1 && <hr className="w-100 mx-0 my-2" />}
                  </div>
                );
              })}
          </div>
        </div>
      );
    };

    const renderCarousel = (element: any, index: number) => {
      return (
        <div key={index} className="carrousel-container mb-10">
          <div
            ref={(el) => (carrouselRefs.current[index] = el)}
            className="preview-cards-carrousel"
            onMouseDown={(e) => handleMouseDown(e, 'carrousel', index)}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none'
            }}
          >
            {element.cards?.map((content: any, id: number) => (
              <div
                key={`${content.title}-${content.URLImage}-${id}`}
                className={
                  isDarkTheme ? 'preview-template-RCS-dark me-2' : 'preview-template-RCS me-2'
                }
              >
                {(content.imageCheck || content.videoCheck) && (
                  <div className="preview-template-img-container-url mb-5">
                    <img
                      alt="Uploaded"
                      aria-disabled
                      className="preview-cideo mw-100 mb-3"
                      src={
                        content.videoCheck && content.URLImagePoster === ''
                          ? '/assets/media/mockups/preview-video.png'
                          : content.imageCheck && content.URLImage === ''
                            ? '/assets/media/mockups/preview-img.png'
                            : content.videoCheck
                              ? content.URLImagePoster
                              : content.URLImage
                      }
                    />
                  </div>
                )}
                <div className="preview-template-header fw-bold">
                  {replaceVariables(content.title, exmplesCampaigns)}
                </div>
                <p className="mb-10">{replaceVariables(content.description, exmplesCampaigns)}</p>
                <div className="text-center">
                  {content.labelURL && (
                    <p className="text-info">
                      <i className="bi bi-globe-americas text-info fw-bolder" />
                      <span className="ms-2 fw-bolder">
                        {replaceVariables(content.labelURL, exmplesCampaigns)}
                      </span>
                    </p>
                  )}
                </div>
                {content.suggestedReply && (
                  <div className="text-center mt-5">
                    <span className="badge badge-light-info fs-6 fw-bold px-4 py-2">
                      {replaceVariables(content.gnlLabel, exmplesCampaigns)}
                    </span>
                  </div>
                )}
                {Array.isArray(content.actions) &&
                  content.actions.map((action: any, i: number) => {
                    const iconClass = getActionIcon(action.type);
                    const label = replaceVariables(action.gnlLabel, exmplesCampaigns);

                    return (
                      <div className="text-center" key={`card-action-${id}-${i}`}>
                        <div className="d-flex align-items-center justify-content-center gap-2 text-info">
                          {iconClass && <i className={`${iconClass} text-info fw-bolder`} />}
                          {label && (
                            <span className="fw-bolder">
                              {replaceVariables(label, exmplesCampaigns)}
                            </span>
                          )}
                        </div>
                        {i !== content.actions.length - 1 && <hr className="w-100 mx-0 my-2" />}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderContentInfo = (element: any, index: number) => {
      return (
        <div key={index} className="onlycard-container mb-10 w-auto">
          <div
            className={
              isDarkTheme
                ? 'preview-template-RCS-dark-contentinfo'
                : 'preview-template-RCS-contentinfo'
            }
          >
            {(element.imageCheck || element.videoCheck || element.fileCheck) && (
              <div className="preview-template-img-container-url-contentinfo">
                {element.imageCheck && (
                  <img
                    alt="Uploaded"
                    className="mw-100"
                    src={
                      isValidImageUrl(element.URLImage)
                        ? element.URLImage
                        : '/assets/media/mockups/preview-img.png'
                    }
                  />
                )}
                {(element.videoCheck || element.fileCheck) && (
                  <img
                    alt="Uploaded"
                    className="mw-100"
                    src={
                      isValidImageUrl(element.URLImagePoster)
                        ? element.URLImagePoster
                        : element.videoCheck
                          ? '/assets/media/mockups/preview-video.png'
                          : '/assets/media/mockups/preview-file.png'
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      );
    };

    useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      } else {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isDragging]);

    useEffect(() => {
      carrouselRefs.current = [];
      if (onlyCardRef.current) {
        onlyCardRef.current.scrollTop = 0;
      }
    }, [data, payload]);

    const contextualData = getContextualData();
    const { elements: templateData, suggestions, context } = contextualData;

    return (
      <>
        <div ref={ref} className={isDarkTheme ? 'wrap-android-dark ms-10' : 'wrap-android ms-10'}>
          {(type.toLowerCase() === 'single' || type.toLowerCase() === 'conversational') && (
            <div
              className="preview-container-RCS"
              ref={onlyCardRef}
              onMouseDown={(e) => handleMouseDown(e, 'onlyCard')}
              style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
            >
              {templateData?.map((item: any, index: number) => {
                if (context === 'builder') {
                  switch (item.type) {
                    case 'richCard':
                      return renderRichCardFromPreview(item, index);
                    case 'richCard_Carrosuel':
                      return renderCarousel(item, index);
                    case 'contenInfo':
                      return renderContentInfo(item, index);
                    default:
                      return null;
                  }
                }

                if (context === 'preview') {
                  if (item.richCard?.standaloneCard) {
                    return renderRichCardFromConversational(item, index);
                  }

                  if (item.type) {
                    switch (item.type) {
                      case 'richCard':
                        return renderRichCardFromPreview(item, index);
                      case 'richCard_Carrosuel':
                        return renderCarousel(item, index);
                      case 'contenInfo':
                        return renderContentInfo(item, index);
                      default:
                        return null;
                    }
                  }
                }

                return null;
              })}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className="d-flex justify-content-end w-100">
                  <div
                    className={`d-flex flex-nowrap suggestion-general ms-10 ${isDragging ? 'dragging' : ''}`}
                    onMouseDown={(e) => handleMouseDown(e, 'suggestions')}
                    ref={suggestionsRef}
                  >
                    {suggestions.map((item: any, index: number) => {
                      const iconClass = getActionIcon(item.type);
                      const label = replaceVariables(
                        item.gnlLabel || item.reply?.text || `Botón ${index + 1}`,
                        exmplesCampaigns
                      );

                      return (
                        <div
                          key={`suggestion-${index}`}
                          className="d-flex align-items-center border border-secondary-subtle rounded-pill px-3 py-1 me-3 bg-light text-nowrap"
                        >
                          {iconClass && <i className={`${iconClass} fs-5 text-info me-2`} />}
                          <span className="fw-semibold">
                            {replaceVariables(label, exmplesCampaigns)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {type.toLowerCase() === 'basic' && (
            <div
              className={
                isDarkTheme
                  ? 'preview-template-RCS-Basic-dark ms-4 mb-3'
                  : 'preview-template-RCS-Basic ms-4 mb-3'
              }
            >
              {payload?._id && !payload?.status && (
                <a className="text-dark d-flex flex-column mb-10">
                  <div className="symbol symbol-60px d-flex justify-content-center">
                    <Icon icon="ki-information-5" size="fs-4x" type="danger" />
                  </div>
                  <div className="fs-5 fw-bold mb-2 text-center">Esta plantilla está inactiva</div>
                </a>
              )}
              <div className="d-flex justify-content-start mb-10">
                {(data?.msgBasic || payload?._id) && (
                  <>
                    <div className="d-flex align-items-center">
                      <div className="symbol symbol-35px symbol-circle me-3 mb-5">
                        <img alt="Pic" src="/assets/media/img/bg-rcs.png" />
                      </div>
                      <div className="d-flex flex-column">
                        <div
                          className={`p-5 rounded ${isDarkTheme ? 'bg-adroid-ms' : 'bg-light'} text-dark fw-semibold w-200px mw-lg-200px text-start`}
                          data-kt-element="message-text"
                        >
                          {replaceVariables(
                            data?.msgBasic || payload?.basic?.text,
                            exmplesCampaigns
                          )}
                        </div>
                        <span className="text-muted fs-7 mt-2">
                          {data?.msgBasic || payload?._id ? getFormattedTime() : 'No message'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* {isValidRoute && (
          <div
            className={`d-flex justify-content-center mt-5 ${routeFallback ? 'ms-10' : 'me-10'}`}
          >
            <button
              className="btn btn-info fw-bolder"
              type="button"
              onClick={() => setShowModal({ show: true, data: { typeModal: 'sms' } })}
            >
              <Icon icon="ki-send" size="fs-1" />
              <span className="ms-2"> Enviar Mensaje de prueba</span>
            </button>
            <SendMessage
              show={showModal.show}
              onRequestClose={handleShowModal}
              typeMockup={'rcs'}
            />
          </div>
        )} */}
      </>
    );
  }
);

export default MockupAndroid;
